import React, {useEffect, useMemo, useState} from "react";
import {makeStyles} from "@material-ui/core";
import GridBlock from "components/GridBlock";
import PropTypes from "prop-types";
import Tr from "translations/Translations"
import {connect} from "react-redux";
import AgreementsService from "services/AgreementsService";
import Loading from "components/Loading";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import {CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon} from "@material-ui/icons";
import IconButton from "components/button/IconButton";
import BasicSearch from "components/BasicSearch";
import Pagination from "components/Pagination";
import Confirm from "components/popup/confirm/Confirm";
import BoldTypography from "components/BoldTypography";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import AgreementCreatePopup from "./AgreementCreatePopup";

const useStyles = makeStyles(theme => ({
    partColumn: theme.components.table.dateColumn,
    buttonColumn: theme.components.table.buttonColumn
}));

function AgreementsAvailablePanel(
    {
        demandId,
        editable,
        agreementsAvailableList,
        agreementsAvailableOrganizationsList
    }) {

    const classes = useStyles();
    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig} = useSearch();
    const [working, setWorking] = useState(false);
    const [confirmNoAgrOpened, setConfirmNoAgrOpened] = useState(false);
    const [createOpened, setCreateOpened] = useState(false);
    const [selected, setSelected] = useState([]);

    useEffect(() => {
        AgreementsService.getAgreementsAvailableOrganizationsList(demandId);
        return () => {
            AgreementsService.clearResources(AgreementsService.AGREEMENTS_AVAILABLE_RESOURCE_ID);
            AgreementsService.clearResources(AgreementsService.AGREEMENTS_AVAILABLE_ORGANIZATIONS_RESOURCE_ID);
        }
    }, [demandId]);

    useEffect(() => {
        setWorking(true);
        const {pagination, search} = searchData;
        AgreementsService.getAgreementsAvailableList(demandId, pagination, search).then(() => (setWorking(false)));
        setSelected([]);
        // eslint-disable-next-line
    }, [searchData, demandId]);

    useDidUpdateEffect(() => {
        if (agreementsAvailableList?.totalCount !== undefined) {
            setTotalCount(agreementsAvailableList.totalCount)
        }
    }, [agreementsAvailableList?.totalCount]);

    const checkClick = (x) => {
        const index = selected.indexOf(x.identity);
        if (index > -1) {
            setSelected(selected.filter(posId => posId !== x.identity))
        } else {
            setSelected(selected => [...selected, x.identity])
        }
    };

    const createNoAgr = () => {
        AgreementsService.clearResources(AgreementsService.AGREEMENTS_AVAILABLE_RESOURCE_ID);
        AgreementsService.createNoAgreements(demandId, selected).finally(() => {
            setPaginationConfig({...searchData.pagination, page: 1})
        });
        setSelected([]);
        setConfirmNoAgrOpened(false);

    };
    const create = (org, oneAgreement) => {
        AgreementsService.clearResources(AgreementsService.AGREEMENTS_AVAILABLE_RESOURCE_ID);
        AgreementsService.createAgreements(demandId, selected, org, oneAgreement).finally(() => {
            setPaginationConfig({...searchData.pagination, page: 1})
        });
        setSelected([]);
        setCreateOpened(false);
    };

    const buttons = useMemo(() => {
        const disabled = !selected.length || working || !agreementsAvailableOrganizationsList.length;
        const buttons = [];
        if (editable) {
            buttons.push(createButton(Tr('agreement_create'), () => {
                setCreateOpened(true)
            }, undefined, disabled));
            buttons.push(createButton(Tr('agreement_create_no_agr'), () => setConfirmNoAgrOpened(true), undefined, disabled));
        }
        return buttons;
    }, [selected, working, editable, agreementsAvailableOrganizationsList]);

    if (!agreementsAvailableList || !agreementsAvailableOrganizationsList) return <Loading/>;

    return (<>
            <GridBlock justify='space-between'>
                <BasicSearch
                    label={Tr('search_part')}
                    searchInProgress={working}
                    doSearch={setSearchConfig}
                />
                <MultiButton buttons={buttons}/>
            </GridBlock>

            <SimpleTable
                headers={
                    [
                        '',
                        Tr('part_number'),
                        Tr('part_name'),
                    ]
                }
                items={
                    agreementsAvailableList.items.map((x, i) => {
                        return [
                            <IconButton
                                disabled={!editable}
                                onClick={() => checkClick(x)}
                                Icon={selected.indexOf(x.identity) > -1 ? CheckBoxIcon : CheckBoxOutlineBlankIcon}
                            />,
                            Tr('part_x', {x: x.order}),
                            x.name
                        ]
                    })
                }
                columnClasses={
                    [
                        classes.buttonColumn,
                        classes.partColumn,
                        undefined,
                    ]
                }
            />
            {
                !agreementsAvailableList.items.length && <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
            }

            <Pagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount} disabled={working}/>
            {
                confirmNoAgrOpened && <Confirm
                    confirmText="agreement_create_confirm"
                    open
                    onCancel={() => setConfirmNoAgrOpened(false)}
                    onConfirm={createNoAgr}
                />
            }
            {
                createOpened && <AgreementCreatePopup
                    onClose={() => setCreateOpened(false)}
                    onSubmit={(org, oneAgreement) => create(org, oneAgreement)}
                />
            }
        </>
    )
}

AgreementsAvailablePanel.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    editable: PropTypes.bool.isRequired,
}

export default connect((state) => ({
    agreementsAvailableList: state.resources[AgreementsService.AGREEMENTS_AVAILABLE_RESOURCE_ID],
    agreementsAvailableOrganizationsList: state.resources[AgreementsService.AGREEMENTS_AVAILABLE_ORGANIZATIONS_RESOURCE_ID]
}), {})(AgreementsAvailablePanel);

